import { useCallback, useEffect, useRef, useState } from "react";
import { DashboardModalFrame } from "./DashboardModalFrame";
import { DashboardButton } from "../primitives/DashboardButton";
import { useToastContext } from "../primitives/ToastProvider";
import { manualPurchaseService } from "../../../../services/manualPurchaseService";
import type { QuoteData, BillingInterval, ProcessingJobStatus } from "../../../../types/manuals";
import { Upload, FileText, AlertTriangle, CheckCircle, XCircle, Loader } from "lucide-react";

type ProcessingPhase = 'pending' | 'validating' | 'sanitizing' | 'analyzing';

type ModalState =
  | { kind: 'empty' }
  | { kind: 'uploading'; progress: number; filename: string }
  | { kind: 'processing'; uploadId: string; jobId: string; phase: ProcessingPhase }
  | { kind: 'quote_ready'; uploadId: string; quote: QuoteData; selectedInterval: BillingInterval; lowConfidence: boolean }
  | { kind: 'checkout_redirect' }
  | { kind: 'payment_success'; uploadId: string }
  | { kind: 'payment_canceled'; uploadId: string; quote: QuoteData }
  | { kind: 'error'; message: string; recoverable: boolean; uploadId?: string }
  | { kind: 'duplicate_warning'; uploadId: string; jobId: string; existingUploadId: string; filename: string };

const MAX_FILE_SIZE = 52_428_800;
const POLL_INTERVAL_MS = 5_000;
const TERMINAL_FAILURE_STATUSES = [
  'validation_failed', 'sanitization_failed', 'analysis_failed',
  'scan_unavailable', 'timeout', 'manual_review', 'malware_detected',
];

function getErrorMessage(status: string): string {
  const messages: Record<string, string> = {
    validation_failed: 'Your file could not be validated. Please check it is a valid, unencrypted PDF under 50 MB.',
    sanitization_failed: 'Your file could not be processed for security reasons. Please try a different file.',
    analysis_failed: 'Document analysis could not be completed. Please try again or contact support.',
    scan_unavailable: 'Our security scanner is temporarily unavailable. Please try again shortly.',
    timeout: 'Processing took too long. Please try again.',
    malware_detected: 'Your file was flagged by our security scanner and cannot be processed.',
    manual_review: 'Your manual has been flagged for manual review. Our team will be in touch within 1 business day.',
  };
  return messages[status] ?? 'An unexpected error occurred. Please try again.';
}

export function ManualsPurchaseModalResponsive({ onClose }: { onClose: () => void }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingActiveRef = useRef(false);
  const [state, setState] = useState<ModalState>({ kind: 'empty' });
  const toast = useToastContext();

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      pollingActiveRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Stripe redirect detection on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    const uploadId = params.get('upload_id');
    if (!checkout || !uploadId) return;
    window.history.replaceState({}, '', window.location.pathname);
    if (checkout === 'success') {
      setState({ kind: 'payment_success', uploadId });
    } else if (checkout === 'canceled') {
      manualPurchaseService.getQuote(uploadId).then((quote) => {
        if (quote) setState({ kind: 'payment_canceled', uploadId, quote });
        else setState({ kind: 'error', message: 'Your pricing quote has expired. Please re-upload your manual.', recoverable: true, uploadId });
      }).catch(() => {
        setState({ kind: 'error', message: 'Could not retrieve your quote. Please try again.', recoverable: true, uploadId });
      });
    }
  }, []);

  function resetToEmpty() {
    if (fileInputRef.current) fileInputRef.current.value = '';
    setState({ kind: 'empty' });
  }

  const startPolling = useCallback((uploadId: string, _jobId: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollingActiveRef.current = true;
    pollRef.current = setInterval(async () => {
      if (!pollingActiveRef.current) return;
      try {
        const status = await manualPurchaseService.getJobStatus(uploadId);
        if (!pollingActiveRef.current) return;
        if (status.status === 'quote_ready') {
          pollingActiveRef.current = false;
          clearInterval(pollRef.current!);
          const quote = await manualPurchaseService.getQuote(uploadId);
          if (quote) setState({ kind: 'quote_ready', uploadId, quote, selectedInterval: 'monthly', lowConfidence: quote.reviewRequired });
        } else if (TERMINAL_FAILURE_STATUSES.includes(status.status as string)) {
          pollingActiveRef.current = false;
          clearInterval(pollRef.current!);
          setState({ kind: 'error', message: getErrorMessage(status.status as string), recoverable: (status.status as string) !== 'malware_detected', uploadId });
        } else {
          setState((prev) => prev.kind === 'processing' ? { ...prev, phase: status.status as ProcessingPhase } : prev);
        }
      } catch { /* transient errors */ }
    }, POLL_INTERVAL_MS);
  }, []);

  const handleFileSelect = useCallback(async (file: File) => {
    if (file.type !== 'application/pdf') {
      setState({ kind: 'error', message: 'Only PDF files are accepted.', recoverable: true });
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setState({ kind: 'error', message: 'File must be under 50 MB.', recoverable: true });
      return;
    }
    setState({ kind: 'uploading', progress: 0, filename: file.name });
    try {
      const intent = await manualPurchaseService.getUploadIntent({ filename: file.name, fileSizeBytes: file.size, mimeType: file.type });
      await manualPurchaseService.uploadToS3(intent.s3UploadUrl, file, (progress) => {
        setState((prev) => prev.kind === 'uploading' ? { ...prev, progress } : prev);
      });
      const result = await manualPurchaseService.completeUpload({ uploadId: intent.uploadId });
      if (result.isDuplicate) {
        setState({ kind: 'duplicate_warning', uploadId: result.uploadId, jobId: result.jobId, existingUploadId: result.duplicateUploadId!, filename: file.name });
        return;
      }
      setState({ kind: 'processing', uploadId: result.uploadId, jobId: result.jobId, phase: 'pending' });
      startPolling(result.uploadId, result.jobId);
    } catch (err) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Upload failed.', recoverable: true });
    }
  }, [startPolling]);

  const handleCheckout = useCallback(async (uploadId: string, quoteId: string, billingInterval: BillingInterval) => {
    setState({ kind: 'checkout_redirect' });
    try {
      const { checkoutUrl } = await manualPurchaseService.createCheckout({ uploadId, quoteId, billingInterval });
      window.location.href = checkoutUrl;
    } catch {
      setState({ kind: 'error', message: 'Could not start checkout. Please try again.', recoverable: true, uploadId });
    }
  }, []);

  const guardedOnClose = useCallback(() => {
    if (['uploading', 'processing', 'checkout_redirect'].includes(state.kind)) return;
    onClose();
  }, [state.kind, onClose]);

  return (
    <DashboardModalFrame
      title="Instant 3D Manual Quote"
      subtitle="Upload a PDF manual and receive an instant pricing quote based on complexity."
      onClose={guardedOnClose}
    >
      <div className="grid gap-4 sm:gap-5">
        {/* ── Empty state: upload zone ── */}
        {state.kind === 'empty' && (
          <section className="rounded-2xl border border-white/10 bg-slate-900/60 p-5">
            <div
              className="grid min-h-[12rem] cursor-pointer place-items-center gap-2 rounded-2xl gradient-outline p-8 text-center transition-colors hover:bg-white/5"
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              aria-label="Upload manual PDF"
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFileSelect(f);
                }}
              />
              <span className="material-symbols-outlined text-4xl text-sky-400">upload_file</span>
              <strong className="text-sm font-semibold text-slate-100">Upload Manual PDF</strong>
              <p className="max-w-md text-sm leading-6 text-slate-400">
                Drop file or click to browse. PDF only, max 50 MB.
              </p>
            </div>
          </section>
        )}

        {/* ── Uploading state ── */}
        {state.kind === 'uploading' && (
          <section className="rounded-2xl border border-white/10 bg-slate-900/60 p-5">
            <div className="flex flex-col items-center justify-center gap-4 py-8">
              <Loader className="h-8 w-8 animate-spin text-sky-400" />
              <strong className="text-sm font-semibold text-slate-100">Uploading {state.filename}...</strong>
              <div className="h-2 w-48 overflow-hidden rounded-full bg-white/10">
                <div className="h-full rounded-full bg-sky-500 transition-all" style={{ width: `${state.progress}%` }} />
              </div>
              <p className="text-xs text-slate-500">{state.progress}%</p>
            </div>
          </section>
        )}

        {/* ── Processing state ── */}
        {state.kind === 'processing' && (
          <section className="rounded-2xl border border-white/10 bg-slate-900/60 p-5">
            <div className="flex flex-col items-center justify-center gap-4 py-8">
              <Loader className="h-8 w-8 animate-spin text-sky-400" />
              <strong className="text-sm font-semibold text-slate-100">
                {state.phase === 'pending' && 'Queued for processing...'}
                {state.phase === 'validating' && 'Validating document...'}
                {state.phase === 'sanitizing' && 'Sanitizing document...'}
                {state.phase === 'analyzing' && 'Analyzing complexity...'}
              </strong>
              <p className="text-xs text-slate-400">This may take a few minutes</p>
            </div>
          </section>
        )}

        {/* ── Duplicate warning state ── */}
        {state.kind === 'duplicate_warning' && (
          <section className="rounded-2xl border border-white/10 bg-slate-900/60 p-5">
            <div className="flex flex-col items-center justify-center gap-4 py-8">
              <span className="material-symbols-outlined rounded-full border border-sky-500/20 bg-sky-500/10 p-3 text-2xl text-sky-300">content_copy</span>
              <strong className="text-sm font-semibold text-slate-100">Duplicate File Detected</strong>
              <p className="text-center text-xs leading-5 text-slate-400">
                This file has already been uploaded. Would you like to continue processing it?
              </p>
              <div className="flex gap-3">
                <DashboardButton variant="secondary" onClick={() => resetToEmpty()} type="button">
                  Cancel
                </DashboardButton>
                <DashboardButton onClick={() => { setState({ kind: 'processing', uploadId: state.uploadId, jobId: state.jobId, phase: 'pending' }); startPolling(state.uploadId, state.jobId); }} type="button">
                  Continue
                </DashboardButton>
              </div>
            </div>
          </section>
        )}

        {/* ── Quote ready state ── */}
        {state.kind === 'quote_ready' && (
          <>
            {state.lowConfidence && (
              <div className="rounded-xl border border-sky-500/20 bg-sky-500/10 px-4 py-3 text-xs leading-5 text-sky-200">
                ⚠️ This quote may require review due to low confidence in the analysis.
              </div>
            )}

            <section className="rounded-2xl border border-white/10 bg-slate-900/60 p-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-white/10 bg-slate-950/70 p-4">
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Pricing Tier</p>
                  <p className="mt-1 text-lg font-semibold text-slate-100">{state.quote.tierLabel}</p>
                </div>
                <div className="rounded-xl border border-white/10 bg-slate-950/70 p-4">
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
                    {state.selectedInterval === 'monthly' ? 'Monthly' : 'Annual'}
                  </p>
                  <p className="mt-1 text-lg font-semibold text-sky-300">
                    {state.selectedInterval === 'monthly' ? state.quote.monthlyPriceFormatted : state.quote.annualPriceFormatted}
                  </p>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={() => setState((prev) => prev.kind === 'quote_ready' ? { ...prev, selectedInterval: 'monthly' } : prev)}
                  className={[
                    "flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
                    state.selectedInterval === 'monthly'
                      ? "bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/30"
                      : "text-slate-400 hover:bg-white/5 hover:text-slate-200",
                  ].join(" ")}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setState((prev) => prev.kind === 'quote_ready' ? { ...prev, selectedInterval: 'annual' } : prev)}
                  className={[
                    "flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
                    state.selectedInterval === 'annual'
                      ? "bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/30"
                      : "text-slate-400 hover:bg-white/5 hover:text-slate-200",
                  ].join(" ")}
                >
                  Annual (20% off)
                </button>
              </div>
            </section>

            {/* Billing disclosure */}
            <section className="rounded-2xl border border-white/10 bg-slate-900/60 p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Billing Terms</p>
              {state.selectedInterval === 'monthly' ? (
                <p className="mt-2 text-xs leading-5 text-slate-400">
                  You will be billed <strong className="text-slate-100">{state.quote.monthlyPriceFormatted}/month for 12 months</strong>.
                  After the 12-month payment period ends, your subscription automatically transitions to <strong className="text-slate-100">$20/month</strong> which
                  covers hosting, updates, and maintenance. You can cancel anytime.
                </p>
              ) : (
                <p className="mt-2 text-xs leading-5 text-slate-400">
                  One-time payment of <strong className="text-slate-100">{state.quote.annualPriceFormatted}</strong> for the full 12-month term.
                  No additional charges during this period. After 12 months, billing transitions to <strong className="text-slate-100">$20/month</strong> for
                  hosting, updates, and maintenance. You can cancel anytime.
                </p>
              )}
            </section>

            <div className="flex flex-wrap justify-end gap-3">
              <DashboardButton variant="secondary" onClick={guardedOnClose} type="button">
                Cancel
              </DashboardButton>
              <DashboardButton onClick={() => handleCheckout(state.uploadId, state.quote.quoteId, state.selectedInterval)} type="button">
                Continue to Payment
              </DashboardButton>
            </div>
          </>
        )}

        {/* ── Checkout redirect state ── */}
        {state.kind === 'checkout_redirect' && (
          <section className="rounded-2xl border border-white/10 bg-slate-900/60 p-5">
            <div className="flex flex-col items-center justify-center gap-4 py-8">
              <Loader className="h-8 w-8 animate-spin text-sky-400" />
              <strong className="text-sm font-semibold text-slate-100">Redirecting to Stripe...</strong>
            </div>
          </section>
        )}

        {/* ── Payment success state ── */}
        {state.kind === 'payment_success' && (
          <section className="rounded-2xl border border-white/10 bg-slate-900/60 p-5">
            <div className="flex flex-col items-center justify-center gap-4 py-8">
              <span className="material-symbols-outlined rounded-full border border-emerald-500/20 bg-emerald-500/10 p-3 text-2xl text-emerald-300">check_circle</span>
              <strong className="text-sm font-semibold text-slate-100">Payment Successful!</strong>
              <p className="text-xs text-slate-400">Your 3D manual is now being built. You'll be notified when it's ready.</p>
            </div>
          </section>
        )}

        {/* ── Payment canceled state ── */}
        {state.kind === 'payment_canceled' && (
          <section className="rounded-2xl border border-white/10 bg-slate-900/60 p-5">
            <div className="flex flex-col items-center justify-center gap-4 py-8">
              <span className="material-symbols-outlined rounded-full border border-white/10 bg-white/5 p-3 text-2xl text-slate-400">cancel</span>
              <strong className="text-sm font-semibold text-slate-100">Payment Canceled</strong>
              <p className="text-xs text-slate-400">Your quote is still valid. You can try again or start over.</p>
              <div className="flex gap-3">
                <DashboardButton variant="secondary" onClick={() => resetToEmpty()} type="button">
                  Start Over
                </DashboardButton>
                <DashboardButton onClick={() => setState({ kind: 'quote_ready', uploadId: state.uploadId, quote: state.quote, selectedInterval: 'monthly', lowConfidence: state.quote.reviewRequired })} type="button">
                  Try Again
                </DashboardButton>
              </div>
            </div>
          </section>
        )}

        {/* ── Error state ── */}
        {state.kind === 'error' && (
          <section className="rounded-2xl border border-white/10 bg-slate-900/60 p-5">
            <div className="flex flex-col items-center justify-center gap-4 py-8">
              <span className="material-symbols-outlined rounded-full border border-rose-500/20 bg-rose-500/10 p-3 text-2xl text-rose-300">error</span>
              <strong className="text-center text-sm font-semibold text-slate-100">{state.message}</strong>
              {state.recoverable ? (
                <DashboardButton onClick={() => resetToEmpty()} type="button">
                  Try Again
                </DashboardButton>
              ) : (
                <p className="text-xs text-slate-400">Please contact support for assistance.</p>
              )}
            </div>
          </section>
        )}
      </div>
    </DashboardModalFrame>
  );
}

export default ManualsPurchaseModalResponsive;
